import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Result } from "antd";

import { isChunkLoadError } from "@/lib/lazyWithReload";
import { captureError } from "@/lib/observability";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureError(error, { componentStack: info.componentStack });
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      // The page's code could not be fetched even after the automatic reload
      // (`lib/lazyWithReload`): say that, not the browser's module error.
      if (isChunkLoadError(this.state.error)) {
        return (
          <Result
            status="warning"
            title="This page could not be loaded"
            subTitle="The console may have been updated, or the connection dropped. Reload to try again."
            extra={
              <Button type="primary" onClick={this.handleReset}>
                Reload
              </Button>
            }
          />
        );
      }
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={this.state.error?.message ?? "Unknown error"}
          extra={
            <Button type="primary" onClick={this.handleReset}>
              Reload
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
